import { inject, Service, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { PkceService } from './pkce-service';
import { AuthToken } from '../types/auth-token';
import { jwtDecode } from 'jwt-decode';
import { User } from '../types/user';

@Service()
export class AuthService {
  private readonly pkce = inject(PkceService);
  private readonly TOKEN_KEY = 'auth_token';
  private authToken: AuthToken | null = null;
  AUTH_STATE = 'authenticate';

  isAuthenticated = signal<boolean>(false);
  user = signal<User|null>(null);

  constructor() {
    const tokenString = localStorage.getItem(this.TOKEN_KEY);

    if (tokenString) {
      this.authToken = JSON.parse(tokenString);
      this.setUser(this.authToken?.id_token || null);
      this.isAuthenticated.set(true);
    }
  }

  // Save the token on a successful login response
  setAuthToken(token: AuthToken): void {
    localStorage.setItem(this.TOKEN_KEY, JSON.stringify(token));
    this.authToken = token;
    this.isAuthenticated.set(true);
    this.setUser(token.id_token);
  }

  // Wipe the token out of storage to terminate the session
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.isAuthenticated.set(false);
    const logoutUrl = new URL('electrosuisse/oidc/end_session', environment.authTenantEndpoint);

    logoutUrl.searchParams.append('id_token_hint', this.getIdToken() || '');
    logoutUrl.searchParams.append('post_logout_redirect_uri', environment.postLogoutRedirectUri);

    location.href = logoutUrl.toString();
  }

  getAccessToken(): string {
    return this.authToken?.access_token || '';
  }

  getRefreshToken(): string {
    return this.authToken?.refresh_token || '';
  }

  getIdToken(): string {
    return this.authToken?.id_token || '';
  }

  getUser() {
    const user = this.user();
    if(user) {
      return {...user};
    }

    return  undefined;
  }

  setUser(idToken: string | null): void {
    if (idToken) {
      const idPayload = this.getDecodedToken(this.getIdToken());

      if (idPayload) {
        this.user.set({
          firstName: idPayload.given_name,
          lastName: idPayload.family_name,
          email: idPayload.email,
          type: idPayload.license.type,
        });
      }
    } else {
      this.user.set(null);
    }
  }

  /**
   * Redirect user to authorization url with given params
   */
  async redirectToAuthentication(redirectTo: string) {
    localStorage.setItem('redirectTo', redirectTo);

    const authorizationUrl = new URL(
      'electrosuisse/oidc/authorize',
      environment.authTenantEndpoint,
    );

    const codeVerifier = this.pkce.generateCodeVerifier();
    const codeChallenge = await this.pkce.generateCodeChallenge(codeVerifier);
    this.setCodeVerifier(codeVerifier);

    authorizationUrl.searchParams.append('response_type', 'code');
    authorizationUrl.searchParams.append('client_id', environment.clientId);
    authorizationUrl.searchParams.append('redirect_uri', environment.redirectUri);
    authorizationUrl.searchParams.append('scope', 'openid profile email app offline_access');
    authorizationUrl.searchParams.append('code_challenge', codeChallenge);
    authorizationUrl.searchParams.append('code_challenge_method', 'S256');
    authorizationUrl.searchParams.append('state', this.AUTH_STATE);
    authorizationUrl.searchParams.append('nonce', 'start');

    location.href = authorizationUrl.toString();
  }

  setCodeVerifier(codeVerifier: string | null): void {
    if (codeVerifier) {
      localStorage.setItem('codeVerifier', codeVerifier);
    } else {
      localStorage.removeItem('codeVerifier');
    }
  }

  getCodeVerifier(): string {
    return localStorage.getItem('codeVerifier') || '';
  }

  getRedirectTo(): string {
    return localStorage.getItem('redirectTo') || '/';
  }

  private getDecodedToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      console.error('Failed to decode token', Error);
      return null;
    }
  }
}
